import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  GlossaryHint,
  Skeleton,
  StatusBadge,
  StatusStepper,
  SubmitButton,
  useSubmittingMutation,
  useToast,
  type StatusTone,
  type StepperStep,
} from "@/design-system/components/ui";
import { formatRelativeTime } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import {
  tpsStripeApi,
  type TpsConnectAccountKey,
  type TpsConnectAccountStatus,
  type TpsConnectStatus,
} from "../api/tpsStripe";

// TPS Stripe onboarding (#7b/#9) — the servicer firm's self-serve "Payment
// setup" tab. The firm sees its two Stripe Connect accounts' live status
// (Clearing = receives borrower payments; Commissions = receives its earned
// servicing fees) and can generate a FRESH Stripe-hosted onboarding link on
// demand — robust against a one-time emailed link expiring. Stripe's hosted
// flow leaves the app (window.location) and returns to ?connect=return. No
// React Query in the portal — the read is plain useState with a cancelled
// guard, writes go through useSubmittingMutation.

const STEPS: StepperStep[] = [
  { key: "clearing", label: "Clearing", hint: "Set up the account that receives borrower payments." },
  { key: "commissions", label: "Commissions", hint: "Set up the account that receives your servicing fees." },
  { key: "done", label: "Done", hint: "Both accounts are verified and ready." },
];

// D1 — backoff schedule (ms before each attempt) for the auto-poll that runs
// when the browser returns from Stripe hosted onboarding (?connect=return).
// getStatus() live-fetches Stripe (2 Accounts.Get per call), so this is bounded
// at 4 attempts (≤8 calls) and stops as soon as onboarding reports complete.
// Cumulative ~2s / 5s / 10s / 18s. The manual "Refresh status" button stays as
// the fallback. NOTE: ?connect=refresh (an Account Link that expired
// mid-onboarding) is a known follow-up — deliberately NOT handled here.
const RETURN_POLL_BACKOFF_MS = [2000, 3000, 5000, 8000];

interface AccountUi {
  /** Plain-language status label shown in the badge. */
  label: string;
  tone: StatusTone;
  /** Hover/keyboard tooltip explaining the status. */
  hint: string;
  /** The firm can generate an onboarding link (account exists and isn't Active). */
  actionable: boolean;
  /** Button label when actionable. */
  buttonLabel: string;
}

// Maps the backend Connect status → plain language. `null` = Phoenix hasn't
// created the account yet.
function accountUi(account: TpsConnectAccountStatus | null): AccountUi {
  if (!account) {
    return {
      label: "Not created yet",
      tone: "neutral",
      hint: "Phoenix hasn't set this account up yet. It will appear here once they do.",
      actionable: false,
      buttonLabel: "",
    };
  }
  switch (account.status) {
    case "Active":
      return {
        label: "Ready",
        tone: "ok",
        hint: "Verified with Stripe and ready to move money.",
        actionable: false,
        buttonLabel: "Ready ✓",
      };
    case "Restricted":
      return {
        label: "Verifying with Stripe",
        tone: "warn",
        hint: "Stripe still needs a little more information before this account is ready.",
        actionable: true,
        buttonLabel: "Continue setup",
      };
    case "Disabled":
      return {
        label: "Action needed",
        tone: "danger",
        hint: "Stripe has paused this account. Continue setup to resolve what it needs.",
        actionable: true,
        buttonLabel: "Continue setup",
      };
    case "Pending":
    default:
      return {
        label: "Not started",
        tone: "warn",
        hint: "Setup hasn't been started yet. It takes just a few minutes.",
        actionable: true,
        buttonLabel: "Set up",
      };
  }
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export function TpsPaymentSetupTab() {
  const toast = useToast();
  const [status, setStatus] = useState<TpsConnectStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [params, setParams] = useSearchParams();
  // Capture the return marker ONCE (lazy init) so cleaning it from the URL
  // below doesn't drop the "welcome back" note on re-render.
  const [returnedFromStripe] = useState(() => params.get("connect") === "return");
  // Which account's setup link is being generated (drives the per-button
  // spinner; the mutation itself is single-flight).
  const [submittingKey, setSubmittingKey] = useState<TpsConnectAccountKey | null>(null);
  // Latest completion flag, read inside the auto-poll loop without
  // re-subscribing the effect.
  const completeRef = useRef(false);

  // Consume ?connect=return so a refresh/re-render can't re-trigger the note.
  // One-shot; reads params imperatively so it needn't re-run.
  useEffect(() => {
    if (params.get("connect")) {
      const next = new URLSearchParams(params);
      next.delete("connect");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load (and re-load, on ?connect=return or a retry) the live status.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsStripeApi.getStatus();
        if (!cancelled) {
          setStatus(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your payment setup."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadNonce]);

  // Keep the completion flag current for the auto-poll loop.
  useEffect(() => {
    completeRef.current = status?.onboardingComplete ?? false;
  }, [status]);

  // D1 — on return from Stripe, poll the live status a few times with backoff so
  // the tab advances to "both accounts ready" without a manual click. Stops as
  // soon as onboarding is complete (or after the bounded attempts).
  useEffect(() => {
    if (!returnedFromStripe) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = (i: number) => {
      if (cancelled || i >= RETURN_POLL_BACKOFF_MS.length) return;
      timer = setTimeout(async () => {
        if (cancelled || completeRef.current) return;
        try {
          const data = await tpsStripeApi.getStatus();
          if (cancelled) return;
          setStatus(data);
          if (data.onboardingComplete) return; // done — stop polling
        } catch {
          // A transient fetch failure shouldn't spin the loop; fall through to
          // the next backoff step. The manual "Refresh status" button remains.
        }
        attempt(i + 1);
      }, RETURN_POLL_BACKOFF_MS[i]);
    };
    attempt(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // returnedFromStripe is captured once; the poll runs at most once per mount.
  }, [returnedFromStripe]);

  const refresh = useSubmittingMutation(
    async () => {
      const data = await tpsStripeApi.getStatus();
      setStatus(data);
    },
    {
      onSuccess: () => toast.success("Status updated."),
      onError: (err) => toast.error(errorMessage(err, "Couldn't refresh your status.")),
    },
  );

  // Begin (or resume) Stripe hosted onboarding for one account — the browser
  // leaves the app for Stripe's secure flow (mirrors PayeePaymentSetupPanel).
  const start = useSubmittingMutation(
    async (account: TpsConnectAccountKey) => {
      const result = await tpsStripeApi.onboardingLink(account);
      if (!result.onboardingUrl) {
        throw new Error("Stripe didn't return a setup link. Please refresh and try again.");
      }
      window.location.href = result.onboardingUrl;
    },
    {
      onError: (err) =>
        toast.error(errorMessage(err, "We couldn't open the setup page. Please try again.")),
    },
  );

  const beginSetup = (account: TpsConnectAccountKey) => {
    setSubmittingKey(account);
    void start.submit(account).finally(() => setSubmittingKey(null));
  };

  // D2 — self-serve "Manage / update" for the ACTIVE Commissions account only
  // (re-opens Stripe's hosted flow to edit bank/business details). Clearing is
  // staff-mediated, so there is no manage action for it.
  const manage = useSubmittingMutation(
    async () => {
      const result = await tpsStripeApi.manageLink();
      if (!result.onboardingUrl) {
        throw new Error("Stripe didn't return a management link. Please refresh and try again.");
      }
      window.location.href = result.onboardingUrl;
    },
    {
      onError: (err) =>
        toast.error(errorMessage(err, "We couldn't open the management page. Please try again.")),
    },
  );

  if (error && !status) {
    return (
      <Card data-testid="tps-payment-setup-error">
        <CardContent className="space-y-space-3">
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setReloadNonce((n) => n + 1)}
            data-testid="tps-payment-setup-retry"
          >
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return <Skeleton className="h-64 w-full" data-testid="tps-payment-setup-loading" />;
  }

  const clearingReady = status.clearing?.status === "Active";
  const commissionsReady = status.commissions?.status === "Active";
  const currentIndex = status.onboardingComplete
    ? 2
    : !clearingReady
      ? 0
      : !commissionsReady
        ? 1
        : 2;
  // Emphasise the first not-yet-ready account so the firm resumes where it
  // left off.
  const nextKey: TpsConnectAccountKey | null =
    status.clearing && status.clearing.status !== "Active"
      ? "clearing"
      : status.commissions && status.commissions.status !== "Active"
        ? "commissions"
        : null;
  const neitherExists = !status.clearing && !status.commissions;

  return (
    <div className="space-y-space-4" data-testid="tps-payment-setup">
      <StatusStepper
        steps={STEPS}
        currentIndex={currentIndex}
        complete={status.onboardingComplete}
        label="Payment setup progress"
        data-testid="tps-payment-setup-stepper"
      />

      {returnedFromStripe && !status.onboardingComplete && (
        <div
          role="status"
          className="rounded-lg border border-status-info bg-status-info/10 p-space-3"
          data-testid="tps-payment-setup-return"
        >
          <p className="text-small font-semibold text-onyx m-0">
            Welcome back — we&apos;re checking Stripe&apos;s verification status.
          </p>
          <p className="mt-space-1 text-caption text-gray-mid m-0">
            What&apos;s next: Stripe can take a few minutes to verify. If an account below still
            shows &ldquo;Verifying,&rdquo; use <span className="font-semibold">Refresh status</span>{" "}
            in a moment, then finish any account that isn&apos;t Ready yet.
          </p>
        </div>
      )}

      {status.onboardingComplete && (
        <Card data-testid="tps-payment-setup-complete">
          <CardContent className="space-y-space-2">
            <div className="flex items-center gap-space-2">
              <CheckCircle2 size={20} strokeWidth={1.5} className="text-status-success" aria-hidden />
              <StatusBadge tone="ok">Both accounts ready</StatusBadge>
            </div>
            <p className="text-small text-onyx m-0">
              You&apos;re all set. Phoenix can route borrower payments to your Clearing account and
              pay your servicing fees to your Commissions account
              {status.onboardingCompletedAt && (
                <> — verified {formatRelativeTime(status.onboardingCompletedAt)}</>
              )}
              .
            </p>
          </CardContent>
        </Card>
      )}

      {/* Two-account explanation. */}
      <Card data-testid="tps-payment-setup-explainer">
        <CardContent className="space-y-space-3">
          <div className="space-y-space-1">
            <h3 className="text-h4 font-semibold text-onyx m-0">Your two payout accounts</h3>
            <p className="text-small text-gray-mid m-0">
              Phoenix pays you through two secure Stripe accounts. Setting them up lets Phoenix route
              money to you automatically — Stripe collects and verifies your business and bank
              details so Phoenix never has to store them.
            </p>
          </div>
          <ul className="list-disc space-y-space-1 pl-space-5 m-0 text-small text-onyx">
            <li>
              <span className="font-semibold">Clearing</span> — receives the payments your borrowers
              make.
            </li>
            <li>
              <span className="font-semibold">Commissions</span> — receives the servicing fees
              you&apos;ve earned.
            </li>
          </ul>
          <div className="rounded-lg border border-border-light bg-white-warm p-space-3">
            <p className="text-caption font-semibold uppercase tracking-wide text-gray-mid m-0">
              What each status means
            </p>
            <dl className="mt-space-2 grid grid-cols-1 gap-space-1 text-caption text-onyx sm:grid-cols-2">
              <StatusMeaning label="Not started" meaning="Setup hasn't begun." />
              <StatusMeaning label="Verifying with Stripe" meaning="Stripe is reviewing your details." />
              <StatusMeaning label="Ready" meaning="Verified and able to move money." />
              <StatusMeaning label="Action needed" meaning="Stripe needs you to resolve something." />
            </dl>
          </div>
        </CardContent>
      </Card>

      {neitherExists ? (
        <Card data-testid="tps-payment-setup-empty">
          <CardContent className="space-y-space-3">
            <p className="text-small text-onyx m-0">
              Your Stripe accounts will appear here once Phoenix sets them up. Check back shortly, or
              refresh to look again.
            </p>
            <SubmitButton
              type="button"
              variant="secondary"
              size="sm"
              pending={refresh.pending}
              pendingLabel="Checking…"
              onClick={() => void refresh.submit()}
              data-testid="tps-payment-setup-refresh"
            >
              Refresh status
            </SubmitButton>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-space-3">
          <AccountCard
            testId="tps-payment-setup-clearing"
            title="Clearing account"
            blurb="Receives borrower payments."
            account={status.clearing}
            isNext={nextKey === "clearing"}
            pending={start.pending && submittingKey === "clearing"}
            disabled={start.pending}
            onSetup={() => beginSetup("clearing")}
            // D2 — Clearing bank edits stay staff-mediated (no self-serve
            // manage); direct the firm to their Phoenix admin instead.
            showAdminNote
          />
          <AccountCard
            testId="tps-payment-setup-commissions"
            title="Commissions account"
            blurb="Receives your earned servicing fees."
            account={status.commissions}
            isNext={nextKey === "commissions"}
            pending={start.pending && submittingKey === "commissions"}
            disabled={start.pending}
            onSetup={() => beginSetup("commissions")}
            // D2 — Commissions can be self-managed once Active.
            canManage
            managePending={manage.pending}
            onManage={() => void manage.submit()}
          />
          <div className="flex flex-wrap items-center gap-space-2">
            <SubmitButton
              type="button"
              variant="secondary"
              size="sm"
              pending={refresh.pending}
              pendingLabel="Checking…"
              onClick={() => void refresh.submit()}
              data-testid="tps-payment-setup-refresh"
            >
              Refresh status
            </SubmitButton>
            <p className="text-caption text-gray-mid m-0">
              Just finished on Stripe? Refresh to pull your latest status.
            </p>
          </div>
        </div>
      )}

      <PrepCard />
      <SetupGuide />
    </div>
  );
}

function StatusMeaning({ label, meaning }: { label: string; meaning: string }) {
  return (
    <div>
      <dt className="inline font-semibold text-onyx">{label}:</dt>{" "}
      <dd className="inline m-0 text-gray-mid">{meaning}</dd>
    </div>
  );
}

function AccountCard({
  testId,
  title,
  blurb,
  account,
  isNext,
  pending,
  disabled,
  onSetup,
  canManage = false,
  managePending = false,
  onManage,
  showAdminNote = false,
}: {
  testId: string;
  title: string;
  blurb: string;
  account: TpsConnectAccountStatus | null;
  isNext: boolean;
  pending: boolean;
  disabled: boolean;
  onSetup: () => void;
  /** D2 — when Active, offer a self-serve "Manage / update" action
   *  (Commissions only). */
  canManage?: boolean;
  managePending?: boolean;
  onManage?: () => void;
  /** D2 — when Active, show the "contact your Phoenix administrator" note
   *  instead of a self-serve action (Clearing only). */
  showAdminNote?: boolean;
}) {
  const ui = accountUi(account);
  const isActive = account?.status === "Active";
  const showManage = isActive && canManage && !!onManage;
  return (
    <Card data-testid={testId} className={isNext ? "border-gold" : undefined}>
      <CardContent className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="min-w-0 space-y-space-1">
          <div className="flex flex-wrap items-center gap-space-2">
            <h3 className="text-h4 font-semibold text-onyx m-0">{title}</h3>
            <StatusBadge tone={ui.tone} hint={ui.hint}>
              {ui.label}
            </StatusBadge>
            {isNext && account && account.status !== "Active" && (
              <span className="text-caption font-semibold text-gold" data-testid={`${testId}-next`}>
                Next step
              </span>
            )}
          </div>
          <p className="text-small text-gray-mid m-0">{blurb}</p>
          {isActive && account?.onboardedAt && (
            <p className="text-caption text-gray-mid m-0">
              Connected {formatRelativeTime(account.onboardedAt)}.
            </p>
          )}
          {isActive && showAdminNote && (
            <p className="text-caption text-gray-mid m-0" data-testid={`${testId}-admin-note`}>
              To update your Clearing account, contact your Phoenix administrator.
            </p>
          )}
        </div>
        <div className="shrink-0">
          {ui.actionable ? (
            <Button
              type="button"
              variant={isNext ? "primary" : "secondary"}
              size="sm"
              loading={pending}
              disabled={disabled}
              onClick={onSetup}
              data-testid={`${testId}-action`}
            >
              {ui.buttonLabel}
            </Button>
          ) : showManage ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={managePending}
              disabled={managePending}
              onClick={onManage}
              data-testid={`${testId}-manage`}
            >
              Manage / update
            </Button>
          ) : isActive ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled
              data-testid={`${testId}-action`}
            >
              Ready ✓
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

// Static "what you'll need" prep card — model of BankDetailsHelp.
function PrepCard() {
  return (
    <Card data-testid="tps-payment-setup-prep">
      <CardContent className="space-y-space-3">
        <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid m-0">
          What you&apos;ll need
        </h3>
        <p className="text-small text-onyx m-0">
          Have these handy before you start — Stripe asks for them to verify your firm:
        </p>
        <ul className="list-disc space-y-space-1 pl-space-5 m-0 text-small text-onyx">
          <li>Your legal business name (exactly as registered).</li>
          <li>
            Your <span className="font-semibold">EIN</span> (Employer Identification Number — your
            business tax ID).
          </li>
          <li>Your business address.</li>
          <li>
            Your bank&apos;s{" "}
            <GlossaryHint term="routing number">routing number</GlossaryHint> and{" "}
            <GlossaryHint term="account number">account number</GlossaryHint> for the account that
            should receive payouts.
          </li>
        </ul>
        <p className="rounded-lg border border-border-light bg-white-warm p-space-3 text-caption text-gray-mid m-0">
          Your details are collected and verified securely by Stripe, our payout partner. Phoenix
          never stores your full bank account number.
        </p>
      </CardContent>
    </Card>
  );
}

// Collapsible, static step-by-step guide.
function SetupGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Button
        variant="text"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="tps-payment-setup-guide-toggle"
      >
        {open ? "Hide the step-by-step guide" : "Show the step-by-step guide"}
      </Button>
      {open && (
        <div
          className="mt-space-2 rounded-lg border border-border-light bg-white-warm p-space-3"
          data-testid="tps-payment-setup-guide"
        >
          <ol className="list-decimal space-y-space-1 pl-space-5 m-0 text-small text-onyx">
            <li>Have your business and bank details ready.</li>
            <li>Set up your Clearing account.</li>
            <li>Set up your Commissions account.</li>
            <li>Come back here — your status refreshes automatically.</li>
            <li>Once both say Ready, you&apos;re all set.</li>
          </ol>
        </div>
      )}
    </div>
  );
}
